"use client";

import { useState, useCallback } from "react";
import type { RingGeometryData } from "@/components/canvas/BootStarfield";
import { EphemeralText } from "@/components/ui/EphemeralText";
import { Magnetic } from "@/components/ui/Magnetic";
import { ExternalLinkIcon, ArrowRightIcon, Button, CopyIcon } from "@/components/ui/Button";
import { Tooltip } from "@/components/ui/Tooltip";
import { getScrimColor } from "@/utils/colors";
import { CONTACT_MOONS } from "@/data/contacts";
import { CardScrim, SideNavArrows, CategoryPill } from "@/components/ui/card";
import { track } from "@vercel/analytics";

// =============================================================================
// CONTACT CARD
// =============================================================================
// Info card for the contact page, using shared card sub-components
// Shows email, LinkedIn, or TEC coaching info based on active moon
// Actions (copy email, open link) are in CTA buttons, not card click

interface ContactCardProps {
  geometry: RingGeometryData | null;
  activeIndex: number; // 0 = email, 1 = tec, 2 = linkedin
  z3Height: number;
  onRotateLeft?: () => void;
  onRotateRight?: () => void;
  orbHoveredSide?: "left" | "right" | null;
  arrowKeyPressed?: "left" | "right" | null;
  isLightMode?: boolean;
}

export function ContactCard({
  geometry,
  activeIndex,
  z3Height,
  onRotateLeft,
  onRotateRight,
  orbHoveredSide,
  arrowKeyPressed,
  isLightMode = false,
}: ContactCardProps) {
  const scrimColor = getScrimColor(isLightMode);
  const [copied, setCopied] = useState(false);
  const [ctaHover, setCtaHover] = useState(false);

  // Get active moon data
  const activeMoon = CONTACT_MOONS[Math.max(0, Math.min(activeIndex, CONTACT_MOONS.length - 1))];

  // Get viewport dimensions from geometry
  const viewportWidth = geometry?.viewportWidth ?? (typeof window !== "undefined" ? window.innerWidth : 400);
  const cardWidth = Math.min(520, viewportWidth * 0.92);

  // Handle CTA action
  const handleAction = useCallback(() => {
    if (activeMoon.action === "copy") {
      navigator.clipboard.writeText("hello@vincentngu.com");
      setCopied(true);
      track("cta_click", { type: "email_copy" });
      setTimeout(() => setCopied(false), 2000);
    } else if (activeMoon.action === "link" && activeMoon.url) {
      track("cta_click", { type: activeMoon.id });
      window.open(activeMoon.url, "_blank", "noopener,noreferrer");
    }
  }, [activeMoon]);

  const showSideNav = !!(onRotateLeft && onRotateRight);
  const hasTestimonial = activeMoon.testimonials && activeMoon.testimonials.length > 0;

  return (
    <>
      {/* Shared scrim gradient */}
      <CardScrim
        scrimColor={scrimColor}
        hasEntered
        idleScrimHeight={280}
      />

      {/* Card container */}
      <div
        className="fixed z-[12] pointer-events-none"
        style={{
          bottom: 28,
          left: (viewportWidth - cardWidth) / 2,
          width: cardWidth,
        }}
      >
        {/* Shared side navigation + center content */}
        <SideNavArrows
          onRotateLeft={onRotateLeft ?? (() => {})}
          onRotateRight={onRotateRight ?? (() => {})}
          orbHoveredSide={orbHoveredSide}
          arrowKeyPressed={arrowKeyPressed}
          accentColor={activeMoon.color}
          isLightMode={isLightMode}
          visible={showSideNav}
          leftLabel="Previous contact"
          rightLabel="Next contact"
        >
          <Magnetic strength={0.06} tiltStrength={3} padding={100}>
            <div
              className="relative flex flex-col items-center justify-end text-center gap-2 py-4"
              style={{ minHeight: 140 }}
            >
              {/* Shared category pill */}
              <CategoryPill
                label={activeMoon.category}
                color={activeMoon.color}
                filled
              />

              {/* Headline */}
              <span
                className="font-bold font-headline leading-tight"
                style={{
                  fontSize: 22,
                  color: isLightMode ? "rgba(0, 0, 0, 0.95)" : "rgba(255, 255, 255, 0.95)",
                }}
              >
                <EphemeralText hoverOpacity={0.5}>
                  {activeMoon.headline}
                </EphemeralText>
              </span>

              {/* Subhead */}
              {activeMoon.subhead && (
                <span
                  className="leading-relaxed"
                  style={{
                    fontSize: 14,
                    color: isLightMode ? "rgba(0, 0, 0, 0.5)" : "rgba(255, 255, 255, 0.45)",
                  }}
                >
                  {activeMoon.subhead}
                </span>
              )}

            </div>
          </Magnetic>
        </SideNavArrows>

        {/* CTA Button + Testimonial tooltip — below arrows */}
        <div className="flex items-center justify-center gap-3 pointer-events-auto pb-4">
          {/* Testimonial tooltip trigger (glass icon button, left of CTA) */}
          {hasTestimonial && (
            <Tooltip
              position="top"
              maxWidth={320}
              content={
                <blockquote className="text-xs leading-relaxed">
                  &ldquo;{activeMoon.testimonials![0].quote}&rdquo;
                  <cite
                    className="block mt-1.5 not-italic text-[11px] font-semibold opacity-60"
                  >
                    — {activeMoon.testimonials![0].author}, {activeMoon.testimonials![0].role}
                  </cite>
                </blockquote>
              }
            >
              <Button
                variant="icon-sm"
                magnetic={false}
                aria-label="Read testimonial"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                </svg>
              </Button>
            </Tooltip>
          )}

          {activeMoon.action === "link" ? (
            <Button
              as="a"
              href={activeMoon.url!}
              target="_blank"
              rel="noopener noreferrer"
              variant="secondary"
              magneticStrength={0.1}
              className="gap-2"
              onClick={handleAction}
              onMouseEnter={() => setCtaHover(true)}
              onMouseLeave={() => setCtaHover(false)}
              style={{
                borderColor: ctaHover ? activeMoon.color : `${activeMoon.color}50`,
                backgroundColor: ctaHover ? activeMoon.color : "transparent",
                color: ctaHover ? "#000" : isLightMode ? "rgba(0,0,0,0.9)" : "white",
                boxShadow: ctaHover ? `0 0 20px ${activeMoon.color}40` : undefined,
                transition: "all 0.2s ease-out",
              }}
            >
              <span>{activeMoon.id === "linkedin" ? "Connect" : "Start Here"}</span>
              <ExternalLinkIcon />
            </Button>
          ) : (
            <Button
              variant="secondary"
              magneticStrength={0.1}
              className="gap-2"
              onClick={handleAction}
              onMouseEnter={() => setCtaHover(true)}
              onMouseLeave={() => setCtaHover(false)}
              style={{
                borderColor: ctaHover ? activeMoon.color : `${activeMoon.color}50`,
                backgroundColor: ctaHover ? activeMoon.color : "transparent",
                color: ctaHover ? "#000" : isLightMode ? "rgba(0,0,0,0.9)" : "white",
                boxShadow: ctaHover ? `0 0 20px ${activeMoon.color}40` : undefined,
                transition: "all 0.2s ease-out",
              }}
            >
              <span>{copied ? "Copied!" : "Copy Email"}</span>
              {!copied && <CopyIcon />}
            </Button>
          )}
        </div>
      </div>
    </>
  );
}
